import type { Locale } from '../types'

/**
 * Client Review — Revision 02 (request #11): Antes e Depois, Guia de pose and
 * weekly / monthly / 12-month challenges. Highest-priority layer of `t()`.
 */
export const review02Strings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Challenges — one model, three durations */
    'eng.ch.duration.weekly': 'Semanal',
    'eng.ch.duration.monthly': 'Mensal',
    'eng.ch.duration.annual': 'Anual · 12 meses',
    'eng.ch.section.weekly': 'Desafios da semana',
    'eng.ch.section.monthly': 'Desafios do mês',
    'eng.ch.section.annual': 'Desafios de 12 meses',
    'eng.ch.period': '{from} – {to}',
    'eng.ch.endsToday': 'Termina hoje',
    'eng.ch.startsIn': 'Começa em {n} dias',
    'eng.ch.ended': 'Encerrado',
    'eng.ch.elapsed': '{elapsed} de {total} dias decorridos',
    'eng.ch.remaining': 'faltam {n} dias',
    'eng.ch.checkpoints': 'Marcos mensais',
    'eng.ch.checkpoints.show': 'Ver marcos mensais',
    'eng.ch.checkpoints.hide': 'Ocultar marcos mensais',
    'eng.ch.checkpoints.flexible':
      'Opcionais. Ficar abaixo de um marco não tira você do desafio anual.',
    'eng.ch.checkpoints.required': 'Este desafio exige cada marco mensal.',
    'eng.ch.cp.reached': 'atingido',
    'eng.ch.cp.below': 'abaixo do marco',
    'eng.ch.cp.current': 'em andamento',
    'eng.ch.cp.upcoming': 'a seguir',
    'eng.ch.cp.aria': '{month}: {progress} de {target} · {state}',
    'eng.ch.unit.yogaSessions': 'sessões',
    'eng.ch.unit.hydrationDays': 'dias',
    'eng.ch.photo.cta': 'Foto do desafio',
    'eng.ch.noneBody': 'Desafios semanais, mensais e de 12 meses aparecem aqui quando começarem.',

    /* Guia de pose */
    'eng.pose.title': 'Foto do desafio',
    'eng.pose.subtitle': 'Opcional. Você decide se salva e se compartilha.',
    'eng.pose.guide': 'Guia de pose',
    'eng.pose.guideHint': 'Referência semitransparente para você se posicionar.',
    'eng.pose.opacity': 'Opacidade do guia',
    'eng.pose.disclaimer':
      'O guia é só uma referência visual. O BEELYV não analisa seu corpo, não avalia a postura e não indica técnica correta.',
    'eng.pose.camera': 'Câmera · protótipo',
    'eng.pose.choose': 'Ou escolha uma foto',
    'eng.pose.noPhoto': 'Tire ou escolha uma foto para salvar no desafio.',
    'eng.pose.save': 'Salvar no desafio',
    'eng.pose.saved': 'Foto salva no desafio. Privada — só você vê.',
    'eng.pose.share': 'Compartilhar foto',
    'eng.pose.shareTitle': 'Compartilhar a foto do desafio?',
    'eng.pose.shareBody':
      'A foto continua privada até você escolher onde compartilhar. Nenhum dado de saúde é incluído.',
    'eng.pose.shareConfirm': 'Escolher onde compartilhar',
    'eng.pose.shareCancel': 'Agora não',
    'eng.pose.missing': 'Este desafio não tem foto.',
    'eng.pose.pose.pigeon': 'Postura do pombo',
    'eng.pose.pose.tree': 'Postura da árvore',
    'eng.pose.capture': 'Tirar foto',
    'eng.pose.retake': 'Refazer',
    'eng.pose.prototypeNote': 'Protótipo: a câmera é simulada e nenhuma imagem é enviada.',
    'eng.pose.viewfinder': 'Visor da câmera com guia de pose: {pose}',
    'ba.prototypeNote': 'Protótipo: a imagem final ainda não é gerada nem armazenada.',

    /* Antes e Depois */
    'ba.title': 'Antes e Depois',
    'ba.subtitle': 'Compare duas fotos com uma moldura BEELYV.',
    'ba.cta': 'Criar Antes e Depois',
    'ba.privacy': 'Privada por padrão. Criar a comparação não publica nada.',
    'ba.before': 'Antes',
    'ba.after': 'Depois',
    'ba.pickBefore': 'Foto do antes',
    'ba.pickAfter': 'Foto do depois',
    'ba.beforeDate': 'Data do antes',
    'ba.afterDate': 'Data do depois',
    'ba.frame': 'Moldura',
    'ba.frame.split': 'Divisão limpa',
    'ba.frame.side': 'Lado a lado',
    'ba.frame.vertical': 'Vertical',
    'ba.frame.deep': 'Verde e dourado',
    'ba.showDates': 'Mostrar datas',
    'ba.showBrand': 'Marca BEELYV discreta',
    'ba.health': 'Dados de saúde na moldura',
    'ba.healthNote':
      'Peso, gordura, biomarcadores e Life Score nunca entram automaticamente. Uma opção controlada por você poderá chegar no futuro.',
    'ba.soon': 'Em breve',
    'ba.preview': 'Prévia',
    'ba.save': 'Salvar comparação',
    'ba.saved': 'Comparação salva. Privada — nada foi publicado.',
    'ba.share': 'Compartilhar',
    'ba.shareTitle': 'Compartilhar esta comparação?',
    'ba.shareBody': 'Ela continua privada até você escolher onde compartilhar.',
    'ba.shareConfirm': 'Escolher onde compartilhar',
    'ba.shareCancel': 'Agora não',
    'ba.error.same': 'Escolha duas fotos diferentes.',
    'ba.error.order': 'A data do depois precisa ser posterior à do antes.',
    'ba.alt': '{label} · {date}',
    'ba.photoOption': 'Foto de {date}',
  },
  'en-US': {
    'eng.ch.duration.weekly': 'Weekly',
    'eng.ch.duration.monthly': 'Monthly',
    'eng.ch.duration.annual': 'Annual · 12 months',
    'eng.ch.section.weekly': "This week's challenges",
    'eng.ch.section.monthly': "This month's challenges",
    'eng.ch.section.annual': '12-month challenges',
    'eng.ch.period': '{from} – {to}',
    'eng.ch.endsToday': 'Ends today',
    'eng.ch.startsIn': 'Starts in {n} days',
    'eng.ch.ended': 'Ended',
    'eng.ch.elapsed': '{elapsed} of {total} days elapsed',
    'eng.ch.remaining': '{n} days left',
    'eng.ch.checkpoints': 'Monthly checkpoints',
    'eng.ch.checkpoints.show': 'Show monthly checkpoints',
    'eng.ch.checkpoints.hide': 'Hide monthly checkpoints',
    'eng.ch.checkpoints.flexible':
      "Optional. Falling short of a checkpoint doesn't remove you from the annual challenge.",
    'eng.ch.checkpoints.required': 'This challenge requires every monthly checkpoint.',
    'eng.ch.cp.reached': 'reached',
    'eng.ch.cp.below': 'below checkpoint',
    'eng.ch.cp.current': 'in progress',
    'eng.ch.cp.upcoming': 'upcoming',
    'eng.ch.cp.aria': '{month}: {progress} of {target} · {state}',
    'eng.ch.unit.yogaSessions': 'sessions',
    'eng.ch.unit.hydrationDays': 'days',
    'eng.ch.photo.cta': 'Challenge photo',
    'eng.ch.noneBody': 'Weekly, monthly and 12-month challenges appear here when they start.',

    'eng.pose.title': 'Challenge photo',
    'eng.pose.subtitle': 'Optional. You decide whether to save and whether to share.',
    'eng.pose.guide': 'Pose guide',
    'eng.pose.guideHint': 'A semi-transparent reference to help you position yourself.',
    'eng.pose.opacity': 'Guide opacity',
    'eng.pose.disclaimer':
      "The guide is only a visual reference. Beelyv doesn't analyse your body, score your pose or indicate correct technique.",
    'eng.pose.camera': 'Camera · prototype',
    'eng.pose.choose': 'Or choose a photo',
    'eng.pose.noPhoto': 'Take or choose a photo to save to the challenge.',
    'eng.pose.save': 'Save to challenge',
    'eng.pose.saved': 'Photo saved to the challenge. Private — only you can see it.',
    'eng.pose.share': 'Share photo',
    'eng.pose.shareTitle': 'Share the challenge photo?',
    'eng.pose.shareBody':
      'The photo stays private until you choose where to share it. No health data is included.',
    'eng.pose.shareConfirm': 'Choose where to share',
    'eng.pose.shareCancel': 'Not now',
    'eng.pose.missing': "This challenge doesn't have a photo.",
    'eng.pose.pose.pigeon': 'Pigeon pose',
    'eng.pose.pose.tree': 'Tree pose',
    'eng.pose.capture': 'Take photo',
    'eng.pose.retake': 'Retake',
    'eng.pose.prototypeNote': 'Prototype: the camera is simulated and no image is uploaded.',
    'eng.pose.viewfinder': 'Camera viewfinder with pose guide: {pose}',
    'ba.prototypeNote': 'Prototype: the final image is not rendered or stored yet.',

    'ba.title': 'Before and After',
    'ba.subtitle': 'Compare two photos with a Beelyv frame.',
    'ba.cta': 'Create Before and After',
    'ba.privacy': 'Private by default. Creating a comparison publishes nothing.',
    'ba.before': 'Before',
    'ba.after': 'After',
    'ba.pickBefore': 'Before photo',
    'ba.pickAfter': 'After photo',
    'ba.beforeDate': 'Before date',
    'ba.afterDate': 'After date',
    'ba.frame': 'Frame',
    'ba.frame.split': 'Clean split',
    'ba.frame.side': 'Side by side',
    'ba.frame.vertical': 'Vertical',
    'ba.frame.deep': 'Green and gold',
    'ba.showDates': 'Show dates',
    'ba.showBrand': 'Discreet Beelyv mark',
    'ba.health': 'Health data in the frame',
    'ba.healthNote':
      'Weight, body fat, biomarkers and Life Score are never added automatically. A user-controlled option may come later.',
    'ba.soon': 'Coming soon',
    'ba.preview': 'Preview',
    'ba.save': 'Save comparison',
    'ba.saved': 'Comparison saved. Private — nothing was published.',
    'ba.share': 'Share',
    'ba.shareTitle': 'Share this comparison?',
    'ba.shareBody': 'It stays private until you choose where to share it.',
    'ba.shareConfirm': 'Choose where to share',
    'ba.shareCancel': 'Not now',
    'ba.error.same': 'Choose two different photos.',
    'ba.error.order': 'The after date must be later than the before date.',
    'ba.alt': '{label} · {date}',
    'ba.photoOption': 'Photo from {date}',
  },
}
